import { state_routes } from "@/data/constants/appSettings";
import { useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface UseNavigationProps {
  loading: boolean;
  authUser: any | null;
  currentUser: any | null;
  organizationId: string | null;
  dataLoading: boolean;
  authInitialized: boolean;
}

// `/admin` is the CAS dashboard: it works across all organizations, so a
// developer can open it from the selection screen without joining one first.
const orgExemptRoutes = ["/organizations", "/organizations/create", "/admin"];
const onboardingRoutes = [
  "/welcome",
  "/setup/teacher",
  "/setup/student",
  "/setup/parent",
];

const publicRoutes = ["/create-account", "/auth/action"];

const bypassAuthRoutes = ["/naeras", "/naeras/hybrid", "/history", "/chart-config", "/job-runs"];

const isRootPath = (path: string): boolean => {
  return path === state_routes.login || /^\/[a-z]{2}\/?$/.test(path) || path === "/";
};

const isPublicRoute = (path: string): boolean => {
  return publicRoutes.some((route) => path.includes(route)) || isRootPath(path);
};

const isOrgExemptRoute = (path: string): boolean => {
  const allExemptRoutes = [...orgExemptRoutes, ...onboardingRoutes, ...bypassAuthRoutes];
  return allExemptRoutes.some((route) => path.startsWith(route));
};

const isOnboardingRoute = (path: string): boolean => {
  return onboardingRoutes.some((route) => path.includes(route));
};

export const useNavigation = ({
  loading,
  authUser,
  currentUser,
  organizationId,
  dataLoading,
  authInitialized,
}: UseNavigationProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navigateToMainPage = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  useEffect(() => {
    if (pathname.startsWith("/scanner/")) return;
    if (!authInitialized) {
      console.log("Auth not yet initialized, waiting...");
      return;
    }

    if (dataLoading) {
      console.log("Data still loading, waiting...");
      return;
    }

    console.log("Navigation decision:", {
      authInitialized,
      authUser: !!authUser,
      currentUser: !!currentUser,
      organizationId,
      pathname,
      dataLoading,
    });

    // --- Case 1: User is not authenticated ---
    if (!authUser) {
      if (!isPublicRoute(pathname) && !bypassAuthRoutes.some((r) => pathname.startsWith(r))) {
        console.log(
          "Unauthenticated user on protected route, redirecting to login"
        );
        navigate(state_routes.login, { replace: true });
      }
      return;
    }

    // --- Case 2: User is authenticated on public route ---
    if (isPublicRoute(pathname)) {
      // Navigate away from public route once we have user data
      if (currentUser) {
        if (organizationId) {
          console.log(
            "Authenticated user on public route with org, going to main page"
          );
          navigateToMainPage();
        } else {
          // No organization, go to org selection
          console.log(
            "Authenticated user on public route without org, going to org selection"
          );
          navigate("/organizations", { replace: true });
        }
      }
      return;
    }

    // --- Case 3: Authenticated user - wait for currentUser data ---
    if (!currentUser) {
      // Still waiting for user data to load, don't navigate yet
      console.log("Authenticated but waiting for user data");
      return;
    }

    // --- Case 4: User is authenticated and onboarded ---

    // If user has no organization and is not on org-exempt routes, redirect to organizations
    if (!organizationId && !isOrgExemptRoute(pathname)) {
      console.log(
        "User without org on non-exempt route, redirecting to org selection"
      );
      navigate("/organizations", { replace: true });
      return;
    }

    // If user has an organization and is on the organizations page, redirect to main page
    if (organizationId && pathname === "/organizations") {
      console.log("User with org on org selection page, going to main page");
      navigateToMainPage();
      return;
    }

    console.log("No navigation needed, staying on current route");
  }, [
    authInitialized,
    authUser,
    currentUser,
    organizationId,
    pathname,
    dataLoading,
  ]);

  return {
    navigateToMainPage,
  };
};
